import { map, isEmpty } from "lodash";

export type TagTypeNode = {
  id: string;
  name?: string | undefined;
  active?: boolean | undefined;
  toggled?: boolean | undefined;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
  isTagType?: boolean | undefined;
  isTagValue?: boolean | undefined;
  tagValue?: { appliedCount?: number; [key: string]: unknown } | undefined;
  children?: TagTypeNode[] | null | undefined;
};

export type TagData = {
  tags?:
    | Array<{
        id: string;
        displayValue?: string | undefined;
        [key: string]: unknown;
      }>
    | undefined;
  name?: string | undefined;
  totalCount?: number | undefined;
};

type BuildArgs = {
  nodeId: string;
  tags: Record<string, TagData | undefined>;
  activeTagId?: string | undefined;
  childrenLoaded: Record<string, boolean>;
  toggledNodes: Record<string, boolean>;
};

const getChildren = ({
  nodeId,
  tags,
  activeTagId,
  childrenLoaded,
  toggledNodes,
}: BuildArgs): TagTypeNode[] => {
  if (!nodeId) {
    return [];
  }

  const childrenData: TagTypeNode[] = map(tags[nodeId]?.tags ?? [], (tag) => {
    const subChildren: TagTypeNode[] = childrenLoaded[tag.id]
      ? getChildren({
          nodeId: tag.id,
          tags,
          activeTagId,
          childrenLoaded,
          toggledNodes,
        })
      : [];

    return {
      id: tag.id,
      name: tag.displayValue,
      active: tag.id === activeTagId,
      toggled: toggledNodes[tag.id] ?? false,
      isTagValue: true,
      tagValue: tag,
      // null = leaf (no chevron) when loaded and empty; [] = branch not yet loaded
      children: childrenLoaded[tag.id]
        ? subChildren.length > 0
          ? subChildren
          : null
        : [],
    };
  });

  return childrenLoaded[nodeId] && isEmpty(childrenData) ? [] : childrenData;
};

const buildTagTypeTree = ({
  nodeId,
  tags,
  activeTagId,
  node,
  childrenLoaded,
  toggledNodes,
}: BuildArgs & { node?: { name?: string } }): TagTypeNode[] => {
  if (!nodeId) {
    return [];
  }

  const nodeName = tags[nodeId]?.name ?? node?.name ?? nodeId;
  const isLoaded = !!childrenLoaded[nodeId];

  const children: TagTypeNode[] = isLoaded
    ? getChildren({ nodeId, tags, activeTagId, childrenLoaded, toggledNodes })
    : [];

  const isEmptyLoaded = isLoaded && children.length === 0;

  return [
    {
      id: nodeId,
      name: nodeName,
      isTagType: true,
      active: activeTagId === nodeId,
      toggled: toggledNodes[nodeId],
      ...(isEmptyLoaded ? { disabled: true } : {}),
      // [] = loaded but empty (disabled root) or branch not yet loaded; null = non-root leaf
      children: isLoaded ? children : [],
    },
  ];
};

export default buildTagTypeTree;
